import { Component, Inject, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  ClientEnvironment,
  ClientNotificationHandlerService,
  ENVIRONMENT,
  Language,
  anyUserProfile,
  emailValidator,
  nameValidator,
  nameValidatorExclude,
} from '@thehubz/client/core';
import { UpdateUserDto, User } from '@thehubz/client/data-access-api';
import { CustomClients } from '@thehubz/shared/branding';
import { UserProfileService } from '../user-profile.service';

@Component({
  selector: 'thehubz-user-personal-information',
  templateUrl: './user-personal-information.component.html',
  styleUrls: ['./user-personal-information.component.scss'],
})
export class UserPersonalInformationComponent implements OnInit, OnChanges {
  @Input() userData!: User;
  @Input() isOtherUser = false;
  @Input() userId?: string;
  userEditGroup: UntypedFormGroup;

  AnyUserProfile = anyUserProfile;

  brandingKey!: CustomClients;
  CustomClients = CustomClients;

  editMode = false;
  languages: Language[] = [];
  loggedInUserEmail?: string;

  constructor(
    private _formBuilder: UntypedFormBuilder,
    private userProfileService: UserProfileService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    @Inject(ENVIRONMENT) env: ClientEnvironment,
  ) {
    this.brandingKey = env.brandingKey ?? CustomClients.Standard;
    this.userEditGroup = this._formBuilder.group({
      userEdit_first_name: ['', [Validators.required, Validators.minLength(2), nameValidator]],
      userEdit_last_name: ['', [Validators.required, Validators.minLength(2), nameValidator]],
      userEdit_position: ['', [Validators.minLength(2), nameValidatorExclude]],
      userEdit_agency: ['', [Validators.minLength(2), nameValidator]],
      userEdit_location: ['', [Validators.minLength(2), nameValidator]],
      userEdit_language: [''],
      userEdit_email: [{ disabled: true }, [Validators.required, Validators.email, emailValidator]],
      userEdit_username: [{ disabled: true }, [Validators.required]],
      userEdit_phone: ['', [Validators.minLength(5)]],
      userEdit_phone_mobile: ['', [Validators.minLength(10)]],
    });
  }

  ngOnInit(): void {
    this.userEditGroup.disable();
    this.languages = [
      {
        id: 'english',
        name: 'English',
      },
      {
        id: 'german',
        name: 'German',
      },
    ];
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.userData) {
      this.loggedInUserEmail = this.userData.email;
      this.patchValues();
    }
  }

  onPersonalInfoEdit(edit: boolean, action?: string) {
    if (edit || action === 'close') {
      this.editMode = edit;
      if (action === 'close') {
        this.patchValues();
        this.userEditGroup.disable();
      } else {
        this.userEditGroup.enable();
      }
    } else {
      if (this.userId && this.userEditGroup.valid) {
        const userObject: UpdateUserDto = {
          firstName: this.userEditGroup.value['userEdit_first_name'],
          lastName: this.userEditGroup.value['userEdit_last_name'],
          // email: this.userEditGroup.value['userEdit_email'],
          profile: {
            displayAgency: this.userEditGroup.value['userEdit_agency'],
            position: this.userEditGroup.value['userEdit_position'],
            location: this.userEditGroup.value['userEdit_location'],
            language: this.userEditGroup.value['userEdit_language']?.id,
            phone: this.userEditGroup.value['userEdit_phone'],
            phoneMobile: this.userEditGroup.value['userEdit_phone_mobile'],
          },
        };

        this.userProfileService.updateUser(this.userId, userObject, 'personalInfo').subscribe({
          next: (response) => {
            if (response) {
              this.userData = response;
              this.patchValues();
              this.clientNotificationHandlerService.successNotification($localize`User personal information updated`);
            }
          },
          error: (error) => {
            this.clientNotificationHandlerService.errorNotification(error);
          },
        });
        this.editMode = edit;
        this.userEditGroup.disable();
      }
    }
  }

  patchValues() {
    if (this.userData) {
      this.userEditGroup.patchValue({
        userEdit_first_name: this.userData.firstName,
        userEdit_last_name: this.userData.lastName,
        userEdit_agency: this.userData.profile.displayAgency,
        userEdit_email: this.userData.email,
        userEdit_username: this.userData.username,
        userEdit_position: this.userData.profile.position,
        userEdit_location: this.userData.profile.location,
        userEdit_language: this.userData.profile.language,
        userEdit_phone: this.userData.profile.phone,
        userEdit_phone_mobile: this.userData.profile.phoneMobile,
      });
    }
  }

  allowNumbersOnly(e: any) {
    const code = e.which ? e.which : e.keyCode;
    if (code > 31 && (code < 48 || code > 57)) {
      e.preventDefault();
    }
  }
}
